import { type SearchOrganizationsAndEntriesApiArg, SupplierRelationDto } from "../../../autogen/bff-api";
import { EMPTY_PERIOD, isReversed, type Period } from "./ContractRangeInputs";

export interface SupplierFilters {
  counties: string[];
  municipalities: string[];
  naceCodes: string[];
  relations: SupplierRelationDto[];
  withoutRelations: SupplierRelationDto[];
  contractFields: Record<string, string[]>;
  lowestValues: Record<string, number>;
  periods: Record<string, Period>;
  endDate: Period;
}

export const NO_SUPPLIER_FILTERS: SupplierFilters = {
  counties: [],
  municipalities: [],
  naceCodes: [],
  relations: [],
  withoutRelations: [],
  contractFields: {},
  lowestValues: {},
  periods: {},
  endDate: EMPTY_PERIOD,
};

const contractFieldEntries = (filters: SupplierFilters): string[] =>
  Object.entries(filters.contractFields).flatMap(([fieldId, values]) => values.map((value) => `${fieldId}:${value}`));

const lowestValueEntries = (filters: SupplierFilters): string[] =>
  Object.entries(filters.lowestValues).map(([fieldId, value]) => `${fieldId}:${value}`);

const typedPeriodEntries = (filters: SupplierFilters): string[] =>
  Object.entries(filters.periods)
    .filter(([, period]) => period.from || period.to)
    .map(([fieldId, period]) => `${fieldId}:${period.from}:${period.to}`);

const periodEntries = (filters: SupplierFilters): string[] =>
  typedPeriodEntries({
    ...filters,
    periods: Object.fromEntries(Object.entries(filters.periods).filter(([, period]) => !isReversed(period))),
  });

const endDateInOrder = (filters: SupplierFilters): Period =>
  isReversed(filters.endDate) ? EMPTY_PERIOD : filters.endDate;

export const isFilteringSuppliers = (filters: SupplierFilters): boolean =>
  filters.counties.length +
    filters.municipalities.length +
    filters.naceCodes.length +
    filters.relations.length +
    filters.withoutRelations.length +
    contractFieldEntries(filters).length +
    lowestValueEntries(filters).length +
    periodEntries(filters).length >
    0 || Boolean(endDateInOrder(filters).from || endDateInOrder(filters).to);

export const supplierFilterSearchArgs = (
  filters: SupplierFilters,
): Pick<
  SearchOrganizationsAndEntriesApiArg,
  | "counties"
  | "municipalities"
  | "naceCodes"
  | "relations"
  | "withoutRelations"
  | "contractField"
  | "contractFieldAtLeast"
  | "contractFieldPeriod"
  | "contractEndDateFrom"
  | "contractEndDateTo"
> => {
  const joined = (values: string[]) => (values.length ? values.join(",") : undefined);
  const contractField = contractFieldEntries(filters);
  const atLeast = lowestValueEntries(filters);
  const periods = periodEntries(filters);
  const endDate = endDateInOrder(filters);
  return {
    counties: joined(filters.counties),
    municipalities: joined(filters.municipalities),
    naceCodes: joined(filters.naceCodes),
    relations: joined(filters.relations),
    withoutRelations: joined(filters.withoutRelations),
    contractField: contractField.length ? contractField : undefined,
    contractFieldAtLeast: atLeast.length ? atLeast : undefined,
    contractFieldPeriod: periods.length ? periods : undefined,
    contractEndDateFrom: endDate.from || undefined,
    contractEndDateTo: endDate.to || undefined,
  };
};

const LIST_PARAMS = ["counties", "municipalities", "naceCodes", "relations", "withoutRelations"] as const;
const CONTRACT_FIELD_PARAM = "contractField";
const AT_LEAST_PARAM = "contractFieldAtLeast";
const PERIOD_PARAM = "contractFieldPeriod";
const END_FROM_PARAM = "contractEndDateFrom";
const END_TO_PARAM = "contractEndDateTo";
export const SUPPLIER_FILTER_PARAMS: string[] = [
  ...LIST_PARAMS,
  CONTRACT_FIELD_PARAM,
  AT_LEAST_PARAM,
  PERIOD_PARAM,
  END_FROM_PARAM,
  END_TO_PARAM,
];

export const supplierFiltersFromParams = (params: URLSearchParams): SupplierFilters => {
  const list = (key: string) => (params.get(key) ?? "").split(",").filter(Boolean);
  const relations = (key: string) => list(key).filter(isSupplierRelation);
  const contractFields: Record<string, string[]> = {};
  params.getAll(CONTRACT_FIELD_PARAM).forEach((entry) => {
    const separator = entry.indexOf(":");
    if (separator <= 0 || separator === entry.length - 1) return;
    const fieldId = entry.slice(0, separator);
    contractFields[fieldId] = [...(contractFields[fieldId] ?? []), entry.slice(separator + 1)];
  });
  const lowestValues: Record<string, number> = {};
  params.getAll(AT_LEAST_PARAM).forEach((entry) => {
    const [fieldId, value] = entry.split(":");
    const number = Number(value);
    if (fieldId && value && Number.isFinite(number)) lowestValues[fieldId] = number;
  });
  const periods: Record<string, Period> = {};
  params.getAll(PERIOD_PARAM).forEach((entry) => {
    const [fieldId, from = "", to = ""] = entry.split(":");
    if (fieldId && (from || to)) periods[fieldId] = { from, to };
  });
  return {
    counties: list("counties"),
    municipalities: list("municipalities"),
    naceCodes: list("naceCodes"),
    relations: relations("relations"),
    withoutRelations: relations("withoutRelations").filter((relation) => !relations("relations").includes(relation)),
    contractFields,
    lowestValues,
    periods,
    endDate: { from: params.get(END_FROM_PARAM) ?? "", to: params.get(END_TO_PARAM) ?? "" },
  };
};

export const writeSupplierFiltersToParams = (filters: SupplierFilters, params: URLSearchParams): void => {
  SUPPLIER_FILTER_PARAMS.forEach((key) => params.delete(key));
  LIST_PARAMS.forEach((key) => {
    if (filters[key].length) params.set(key, filters[key].join(","));
  });
  contractFieldEntries(filters).forEach((entry) => params.append(CONTRACT_FIELD_PARAM, entry));
  lowestValueEntries(filters).forEach((entry) => params.append(AT_LEAST_PARAM, entry));
  typedPeriodEntries(filters).forEach((entry) => params.append(PERIOD_PARAM, entry));
  if (filters.endDate.from) params.set(END_FROM_PARAM, filters.endDate.from);
  if (filters.endDate.to) params.set(END_TO_PARAM, filters.endDate.to);
};

const isSupplierRelation = (value: string): value is SupplierRelationDto =>
  (Object.values(SupplierRelationDto) as string[]).includes(value);
